import React, { useState, useMemo, useCallback } from 'react';
import { getGroupSettlementStats } from '../services/apiService';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import GroupSettlementDetailModal from '../components/GroupSettlementDetailModal';

const formatNumber = (v) => {
  if (v === null || v === undefined || v === '') return '0';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  if (isNaN(n)) return '0';
  return n.toLocaleString('ko-KR');
};

const parseNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

const stripMarkup = (v) => {
  if (!v) return '';
  return String(v).replace(/<[^>]*>/g, '').trim();
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const GroupSettlement = () => {
  const today = new Date();
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);

  const [startDate, setStartDate] = useState(formatDate(today));
  const [endDate, setEndDate] = useState(formatDate(oneMonthLater));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupRows, setGroupRows] = useState([]);
  const [individualRows, setIndividualRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('group');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Group Info Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSaleNo, setDrawerSaleNo] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');

  // Group detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalGroup, setDetailModalGroup] = useState(null);

  const openGroupDrawer = (group) => {
    const firstSale = group.sales && group.sales.length > 0 ? group.sales[0] : null;
    const saleNo = firstSale?.판매번호 || null;
    if (!saleNo) return;
    setDrawerSaleNo(saleNo);
    setDrawerTitle(group.행사단체명 || '단체정보');
    setDrawerOpen(true);
  };

  const openReservationDetail = (sale) => {
    const orderNo = sale?.주문번호;
    const saleNo = sale?.판매번호;
    if (!orderNo) return;
    window.open(
      `/reservation-detail/${orderNo}?saleNo=${saleNo}&productName=${encodeURIComponent(sale?.행사단체명 || '')}`,
      '_blank'
    );
  };

  const openDetailModal = (group) => {
    if (!group) return;
    setDetailModalGroup(group);
    setDetailModalOpen(true);
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getGroupSettlementStats({ startDate, endDate });
      if (result && result.groupRows) {
        setGroupRows(result.groupRows);
        setIndividualRows(result.individualRows || []);
      } else if (Array.isArray(result)) {
        setGroupRows(result);
        setIndividualRows([]);
      } else {
        setGroupRows([]);
        setIndividualRows([]);
      }
      setSearched(true);
    } catch (err) {
      setError(err?.message || '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const setQuickPeriod = (type) => {
    const t = new Date();
    if (type === 'today') {
      setStartDate(formatDate(t));
      setEndDate(formatDate(t));
    } else if (type === 'week') {
      const end = new Date(t);
      end.setDate(t.getDate() + 7);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    } else if (type === 'month') {
      const end = new Date(t);
      end.setMonth(t.getMonth() + 1);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    } else if (type === '3months') {
      const end = new Date(t);
      end.setMonth(t.getMonth() + 3);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    }
  };

  // Group rows by 행사단체번호
  const groupedData = useMemo(() => {
    const map = new Map();
    groupRows.forEach((row) => {
      const key = row.행사단체번호;
      if (!map.has(key)) {
        map.set(key, {
          행사단체번호: key,
          행사단체명: row.행사단체명 || '',
          시작일: row.시작일 || '',
          종료일: row.종료일 || '',
          정산기준일: row.정산기준일 || '',
          판매건수: parseNumber(row.판매건수),
          행사인원: row.행사인원 || '0',
          행사인원2: parseNumber(row.행사인원2),
          취소건수: parseNumber(row.취소건수),
          취소인원: row.취소인원 || '0/0/0',
          인솔자: row.인솔자 || '',
          수배담당자: row.수배담당자 || '',
          수배담당부서: row.수배담당부서 || '',
          원화환산액: parseNumber(row.원화환산액),
          출금액: parseNumber(row.출금액),
          미지급액: parseNumber(row.미지급액),
          알선수익: parseNumber(row.알선수익),
          대매수익: parseNumber(row.대매수익),
          기타수익: parseNumber(row.기타수익),
          수익합계: parseNumber(row.수익합계),
          최종수익: parseNumber(row.최종수익),
          마감상태: row.마감상태 || '',
          마감상태코드: row.마감상태코드 || '',
          마감상태명: row.마감상태명 || '',
          처리일시: row.처리일시 || '',
          처리자: row.처리자 || '',
          sales: [],
          판매NET합계: 0,
          입금액합계: 0,
          미수액합계: 0,
          대리점수수료합계: 0,
          계산최종수익: 0,
        });
      }
      map.get(key).sales.push(row);
    });
    const groups = Array.from(map.values());
    groups.forEach((g) => {
      let netSum = 0;
      let depositSum = 0;
      let commissionSum = 0;
      (g.sales || []).forEach((sale) => {
        const n = parseNumber(sale.판매현황NET);
        const d = parseNumber(sale.판매현황입금액);
        netSum += n;
        depositSum += d;
        commissionSum += parseNumber(sale.대리점수수료);
      });
      g.판매NET합계 = netSum;
      g.입금액합계 = depositSum;
      g.미수액합계 = netSum - depositSum;
      g.대리점수수료합계 = commissionSum;
      g.계산최종수익 = g.최종수익 - g.미수액합계;
    });
    return groups;
  }, [groupRows]);

  // Filter
  const filteredGroups = useMemo(() => {
    let result = groupedData;
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      result = result.filter((g) =>
        String(g.행사단체명).toLowerCase().includes(kw) ||
        String(g.행사단체번호).toLowerCase().includes(kw) ||
        String(g.인솔자).toLowerCase().includes(kw) ||
        String(g.수배담당자).toLowerCase().includes(kw) ||
        g.sales.some((s) => String(s.대리점 || '').toLowerCase().includes(kw))
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((g) => {
        if (statusFilter === 'closed') return g.마감상태코드 === '1';
        if (statusFilter === 'open') return g.마감상태코드 === '0';
        return true;
      });
    }
    return result;
  }, [groupedData, keyword, statusFilter]);

  // Sort
  const sortedGroups = useMemo(() => {
    if (!sortKey) return filteredGroups;
    const sorted = [...filteredGroups];
    sorted.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === '최종수익') {
        va = a.계산최종수익;
        vb = b.계산최종수익;
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '');
      vb = String(vb || '');
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return sorted;
  }, [filteredGroups, sortKey, sortDir]);

  const filteredIndividual = useMemo(() => {
    if (!keyword.trim()) return individualRows;
    const kw = keyword.trim().toLowerCase();
    return individualRows.filter((r) =>
      String(r.판매번호 || '').toLowerCase().includes(kw) ||
      String(r.예약담당 || '').toLowerCase().includes(kw)
    );
  }, [individualRows, keyword]);

  // Summary
  const summary = useMemo(() => {
    const s = {
      totalGroups: filteredGroups.length,
      totalSales: 0,
      totalPersonnel: 0,
      totalNet: 0,
      totalDeposit: 0,
      totalMisu: 0,
      totalAgencyCommission: 0,
      totalAdvance: 0,
      totalDisburse: 0,
      totalUnpaid: 0,
      totalProfit: 0,
      totalFinalProfit: 0,
      closedCount: 0,
      openCount: 0,
    };
    filteredGroups.forEach((g) => {
      s.totalSales += g.판매건수;
      s.totalPersonnel += g.행사인원2;
      g.sales.forEach((sale) => {
        const net = parseNumber(sale.판매현황NET);
        const dep = parseNumber(sale.판매현황입금액);
        s.totalNet += net;
        s.totalDeposit += dep;
        s.totalMisu += net - dep;
        s.totalAgencyCommission += parseNumber(sale.대리점수수료);
      });
      s.totalAdvance += g.원화환산액;
      s.totalDisburse += g.출금액;
      s.totalUnpaid += g.미지급액;
      s.totalProfit += g.수익합계;
      s.totalFinalProfit += g.계산최종수익;
      if (g.마감상태코드 === '1') s.closedCount++;
      else s.openCount++;
    });
    return s;
  }, [filteredGroups]);

  const toggleGroup = (groupNo) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupNo)) next.delete(groupNo);
      else next.add(groupNo);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(sortedGroups.map((g) => g.행사단체번호)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortKey !== field) return <span className="text-gray-300 ml-0.5">↕</span>;
    return <span className="text-blue-500 ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const getStatusBadge = (code, name) => {
    if (code === '1') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">정산마감</span>;
    if (code === '0') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">미마감</span>;
    return <span className="text-xs text-gray-500">{name || '-'}</span>;
  };

  const getReservationBadge = (code, name) => {
    const cleaned = stripMarkup(name);
    const colorMap = {
      '049001': 'bg-blue-100 text-blue-700',
      '049002': 'bg-amber-100 text-amber-700',
      '049011': 'bg-emerald-100 text-emerald-700',
      '049019': 'bg-indigo-100 text-indigo-700',
      '049021': 'bg-red-100 text-red-700',
      '049022': 'bg-red-100 text-red-700',
      '049029': 'bg-red-100 text-red-700',
    };
    const cls = colorMap[code] || 'bg-gray-100 text-gray-600';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{cleaned || '-'}</span>;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">단체정산</h1>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">시작일</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">종료일</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', key: 'today' },
              { label: '1주일', key: 'week' },
              { label: '1개월', key: 'month' },
              { label: '3개월', key: '3months' },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setQuickPeriod(p.key)}
                className="px-3 py-2 text-xs rounded-lg border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading ? '조회 중...' : '조회'}
          </button>
        </div>
        {searched && (
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            <input
              type="text"
              placeholder="단체명, 단체번호, 대리점 검색..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체 마감상태</option>
              <option value="open">미마감</option>
              <option value="closed">정산마감</option>
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      )}

      {searched && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: '단체 수', value: summary.totalGroups, unit: '건', color: 'blue' },
              { label: '총 판매건수', value: summary.totalSales, unit: '건', color: 'indigo' },
              { label: '총 인원', value: summary.totalPersonnel, unit: '명', color: 'violet' },
              { label: '판매NET 합계', value: summary.totalNet, unit: '원', color: 'emerald' },
              { label: '최종수익 합계', value: summary.totalFinalProfit, unit: '원', color: summary.totalFinalProfit >= 0 ? 'emerald' : 'red' },
            ].map((card, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                <div className="text-xs text-gray-500 mb-1">{card.label}</div>
                <div className={`text-lg font-bold ${card.color === 'red' ? 'text-red-600' : card.color === 'emerald' ? 'text-emerald-600' : 'text-gray-900'}`}>
                  {typeof card.value === 'number' ? formatNumber(card.value) : card.value}
                  {card.unit && <span className="text-xs font-normal text-gray-500 ml-1">{card.unit}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 shadow-sm p-1">
            <button
              type="button"
              onClick={() => setActiveTab('group')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'group' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              단체별 정산 ({sortedGroups.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('individual')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'individual' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              개별 판매 ({filteredIndividual.length})
            </button>
          </div>

          {/* Group Tab */}
          {activeTab === 'group' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-semibold text-gray-800">단체별 정산 내역</div>
              </div>
              {sortedGroups.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">조회된 단체 정산 데이터가 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('행사단체번호')}>단체번호 <SortIcon field="행사단체번호" /></th>
                        <th className="px-2 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('행사단체명')}>단체명 <SortIcon field="행사단체명" /></th>
                        <th className="px-2 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('시작일')}>시작일 <SortIcon field="시작일" /></th>
                        <th className="px-2 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">종료일</th>
                        <th className="px-2 py-2 text-center font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">판매건수</th>
                        <th className="px-2 py-2 text-center font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">행사인원</th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">판매NET 합계</th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">입금액 합계</th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">미수액 합계</th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">대리점수수료 합계</th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('원화환산액')}>전도금 <SortIcon field="원화환산액" /></th>
                        <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('계산최종수익')}>최종수익 <SortIcon field="계산최종수익" /></th>
                        <th className="px-2 py-2 text-center font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">마감상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGroups.map((group, gIdx) => (
                        <tr
                          key={group.행사단체번호}
                          className={`${gIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/50 transition-colors`}
                          onClick={() => openDetailModal(group)}
                        >
                          <td className="px-2 py-2 border-b border-gray-100">
                            <button
                              type="button"
                              className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
                              onClick={(e) => { e.stopPropagation(); openGroupDrawer(group); }}
                              title="단체 상세 보기"
                            >
                              {group.행사단체번호}
                            </button>
                          </td>
                          <td className="px-2 py-2 border-b border-gray-100 font-medium text-gray-900 max-w-[200px] truncate" title={group.행사단체명}>{group.행사단체명}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-gray-700">{group.시작일}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-gray-700">{group.종료일}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-center text-gray-700">{formatNumber(group.판매건수)}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-center text-gray-700">{group.행사인원}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-right text-gray-700">{formatNumber(group.판매NET합계)}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-right text-gray-700">{formatNumber(group.입금액합계)}</td>
                          <td className={`px-2 py-2 border-b border-gray-100 text-right ${group.미수액합계 > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>{formatNumber(group.미수액합계)}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-right text-gray-700">{formatNumber(group.대리점수수료합계)}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-right text-gray-700">{formatNumber(group.원화환산액)}</td>
                          <td className={`px-2 py-2 border-b border-gray-100 text-right font-bold ${group.계산최종수익 < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatNumber(group.계산최종수익)}</td>
                          <td className="px-2 py-2 border-b border-gray-100 text-center">{getStatusBadge(group.마감상태코드, group.마감상태명)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Footer totals */}
                    <tfoot className="bg-gray-100 font-semibold">
                      <tr>
                        <td className="px-2 py-2 border-t-2 border-gray-300" colSpan={4}>합계</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-center">{formatNumber(summary.totalSales)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-center">{formatNumber(summary.totalPersonnel)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-right">{formatNumber(summary.totalNet)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-right">{formatNumber(summary.totalDeposit)}</td>
                        <td className={`px-2 py-2 border-t-2 border-gray-300 text-right ${summary.totalMisu > 0 ? 'text-red-600' : 'text-gray-700'}`}>{formatNumber(summary.totalMisu)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-right">{formatNumber(summary.totalAgencyCommission)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300 text-right">{formatNumber(summary.totalAdvance)}</td>
                        <td className={`px-2 py-2 border-t-2 border-gray-300 text-right ${summary.totalFinalProfit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatNumber(summary.totalFinalProfit)}</td>
                        <td className="px-2 py-2 border-t-2 border-gray-300"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Individual Sales Tab */}
          {activeTab === 'individual' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-semibold text-gray-800">개별 판매 (단체 미배정)</div>
                <div className="text-xs text-gray-500 mt-0.5">행사단체에 배정되지 않은 개별 판매 건입니다.</div>
              </div>
              {filteredIndividual.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">조회된 개별 판매 데이터가 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">판매번호</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">행사시작일</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">예약담당</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-600 border-b border-gray-200">예약상태</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-600 border-b border-gray-200">인원</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">판매금액</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">항공료</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIndividual.map((row, idx) => (
                        <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/30`}>
                          <td className="px-3 py-2 border-b border-gray-100">
                            {row.판매번호 ? (
                              <button
                                type="button"
                                className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
                                onClick={() => {
                                  const orderNo = row?.주문번호;
                                  const saleNo = row?.판매번호;
                                  if (!orderNo) return;
                                  window.open(`/reservation-detail/${orderNo}?saleNo=${saleNo}`, '_blank');
                                }}
                                title="예약 상세 보기"
                              >
                                {row.판매번호}
                              </button>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{row.행사시작일 || '-'}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-800">{row.예약담당 || '-'}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{stripMarkup(row.예약상태) || '-'}</span>
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100 text-center text-gray-700">{formatNumber(row.인원)}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-right text-gray-800">{formatNumber(row.판매금액)}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-right text-gray-700">{formatNumber(row.항공료)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 font-semibold">
                      <tr>
                        <td className="px-3 py-2 border-t-2 border-gray-300" colSpan={4}>합계</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-center">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.인원), 0))}</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-right">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.판매금액), 0))}</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-right">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.항공료), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!searched && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-20 text-center">
          <div className="text-gray-400 text-sm">기간을 선택하고 조회 버튼을 눌러주세요.</div>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-20 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            데이터를 불러오는 중...
          </div>
        </div>
      )}
      {/* Group Info Drawer */}
      <GroupInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        saleNo={drawerSaleNo}
        title={drawerTitle}
      />
      <GroupSettlementDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        group={detailModalGroup}
        onOpenReservationDetail={openReservationDetail}
      />
    </div>
  );
};

export default GroupSettlement;
